// Archivo: src/components/ProfessionalDropdown.js
import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaEllipsisV } from 'react-icons/fa';
import { ModalContext } from '../context/ModalContext';
import { TabContext } from '../context/TabContext';
import { ModalHeaderContext } from '../context/ModalHeaderContext';
import ShareModal from '../components/ShareModal';
import './ProfessionalDropdown.css';

// Seguimos utilizando el hook para scroll, en caso de otros contextos
import useCloseOnScroll from '../hooks/useCloseOnScroll';

const ProfessionalDropdown = ({ professional }) => {
  // Estado para manejar si el dropdown está abierto
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Referencias para el botón y el menú
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  // Estado para almacenar la posición del menú desplegable
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0 });

  const { openModal, closeModal, modalStacks } = useContext(ModalContext);
  const { activeTab } = useContext(TabContext);

  // ▶ Paso 2.b: Traemos las funciones de registro desde el nuevo contexto
  const { registerHeaderActions, unregisterHeaderActions } = useContext(ModalHeaderContext);

  // Estado para guardar el target para detectar scroll; en este caso seguimos utilizando window
  const [scrollContainer, setScrollContainer] = useState(null);
  useEffect(() => {
    // Intentamos buscar primero un contenedor (como ".home-container" o ".modal-container")
    let container = document.querySelector('.modal-container');
    if (container && container.scrollHeight > container.clientHeight) {
      console.log('[ProfessionalDropdown] Se encontró contenedor modal scrollable, se usará como target:', container);
      setScrollContainer(container);
      return;
    }
    container = document.querySelector('.home-container');
    if (container && container.scrollHeight > container.clientHeight) {
      console.log('[ProfessionalDropdown] Contenedor home encontrado y es scrollable, se usará como target:', container);
      setScrollContainer(container);
    } else {
      console.log('[ProfessionalDropdown] No se encontró contenedor scrollable adecuado, se usará window.');
      setScrollContainer(window);
    }
  }, [professional]);

  // ─── Listeners para cerrar dropdown si hay scroll o clic fuera ────────────

  // Función para alternar el dropdown
  const toggleDropdown = () => {
    setDropdownOpen(prev => !prev);
    console.log('[ProfessionalDropdown] toggleDropdown, nuevo estado:', !dropdownOpen);
  };

  // Calcular la posición del menú cuando se abre el dropdown
  useEffect(() => {
    if (dropdownOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuStyle({
        top: rect.bottom + (window.scrollY || 0),
        left: rect.right - 200 + (window.scrollX || 0), // Ajusta según el ancho del menú
      });
      console.log('[ProfessionalDropdown] Dropdown abierto. Posición calculada:', {
        top: rect.bottom + window.scrollY,
        left: rect.right - 200 + window.scrollX,
      });
    }
  }, [dropdownOpen]);

  // Cerrar el dropdown si se hace clic fuera del botón o del menú
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!menuRef.current || !menuRef.current.contains(event.target))
      ) {
        console.log('[ProfessionalDropdown] Clic fuera detectado, se cierra el dropdown');
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Listener para eventos "wheel" (para escritorio)
  useEffect(() => {
    const handleWheel = (event) => {
      if (dropdownOpen) {
        console.log('[ProfessionalDropdown] Se cierra el dropdown por wheel event.');
        setDropdownOpen(false);
      }
    };

    window.addEventListener('wheel', handleWheel);
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [dropdownOpen]);

  // Listener para eventos "touchmove" (para móviles)
  useEffect(() => {
    const handleTouchMove = (event) => {
      console.log('[ProfessionalDropdown] Evento touchmove detectado.');
      if (dropdownOpen) {
        console.log('[ProfessionalDropdown] Se cierra el dropdown por touchmove event.');
        setDropdownOpen(false);
      }
    };

    window.addEventListener('touchmove', handleTouchMove);
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [dropdownOpen]);

  // Se mantiene la integración del hook para otros casos
  useCloseOnScroll(() => {
    if (dropdownOpen) {
      console.log('[ProfessionalDropdown] Se cierra el dropdown por scroll (hook)');
      setDropdownOpen(false);
    }
  }, scrollContainer);

  // ─────────────────────────────────────────────────────────────────────────────
  // ▶ Paso 2.c: Determinar el ID del modal “Profesional” que está abierto
  // Buscamos en la pila activa una entrada cuyo metadata.profId coincida
  // con professional.id y sacamos su `id` único.
  const professionalEntry = (modalStacks[activeTab] || [])
    .slice()
    .reverse()
    .find(m => m.metadata?.profId === String(professional.id));
  const professionalModalId = professionalEntry?.id;

  // ─────────────────────────────────────────────────────────────────────────────
  // ▶ Definimos handleShare y handleReport ANTES de usarlos en el efecto
  const handleShare = useCallback(() => {
    if (!professional) {
      console.error('No se proporcionó información del profesional para compartir');
      return;
    }
    const shareUrl = `${window.location.origin}/professional/${professional.id}?modal=true`;
    const shareText = `¡Échale un vistazo a este profesional: ${professional.nombre}`;
    console.log('[ProfessionalDropdown] Abriendo ShareModal con:', { shareUrl, shareText });
    openModal(
      <ShareModal
        onClose={() => closeModal(activeTab)}
        shareUrl={shareUrl}
        shareText={shareText}
      />,
      {
        tab: activeTab,
        keepHeader: true,
      }
    );
    setDropdownOpen(false);
  }, [openModal, closeModal, activeTab, professional]);

  const handleReport = useCallback(() => {
    console.log('[ProfessionalDropdown] Denunciar perfil');
    setDropdownOpen(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // ▶ Paso 2.d: Registrar los callbacks en el contexto de cabeceras al montar…
  useEffect(() => {
    if (!professionalModalId) return;

    // Definimos las funciones que queremos que aparezcan en el header:
    const onShare = () => {
      console.log('[HeaderAction] Compartir profesional desde callbacks registrados.');
      handleShare();
    };
    const onReport = () => {
      console.log('[HeaderAction] Denunciar perfil desde callbacks registrados.');
      handleReport();
    };

    // Registramos estas dos acciones bajo el modalId
    registerHeaderActions(professionalModalId, { onShare, onReport });

    // Cuando el componente se desmonte o cambie de modalId, desregistramos
    return () => {
      unregisterHeaderActions(professionalModalId);
    };
  }, [
    professionalModalId,
    registerHeaderActions,
    unregisterHeaderActions,
    handleShare,
    handleReport
  ]);

  // Renderización del menú en un portal para superponerlo
  const dropdownMenuElement = (
    <div
      ref={menuRef}
      className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}
      style={{ ...menuStyle, position: 'absolute', zIndex: 20000 }}
    >
      <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleShare(); }}>
        Compartir
      </button>
      <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleReport(); }}>
        Denunciar perfil
      </button>
    </div>
  );

  return (
    <div className={`dropdown ${dropdownOpen ? 'show' : ''}`} ref={dropdownRef}>
      <button
        className="btn btn-link dropdown-toggle"
        type="button"
        onClick={toggleDropdown}
        aria-expanded={dropdownOpen}
      >
        <FaEllipsisV size={20} />
      </button>
      {dropdownOpen && ReactDOM.createPortal(dropdownMenuElement, document.body)}
    </div>
  );
};

export default ProfessionalDropdown;
